import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/app/PageShell";
import { Buildings, Users, FilesIcon, Database, CheckCircle, Pause, Hourglass } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PlatformDashboard() {
    const [s, setS] = useState(null);

    useEffect(() => {
        api.get("/platform/stats").then(({ data }) => setS(data)).catch((e) => toast.error(extractError(e)));
    }, []);

    if (!s) return <PageContainer><div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Loading…</div></PageContainer>;

    const Tile = ({ label, value, icon: Icon, hint, testid }) => (
        <div className="brut-card p-6" data-testid={testid}>
            <div className="flex items-start justify-between mb-3">
                <div className="label-overline">{label}</div>
                <Icon size={18} weight="duotone" className="text-muted-foreground" />
            </div>
            <div className="text-4xl font-bold tracking-tight">{value}</div>
            {hint && <div className="text-xs text-muted-foreground mt-2 font-mono">{hint}</div>}
        </div>
    );

    return (
        <PageContainer>
            <PageHeader
                overline="Platform Control"
                title="Mission control"
                description="Every organization, user, form, and record across the entire platform."
                testid="platform-dashboard-header"
            />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                <Tile label="Organizations" value={s.orgs_total} icon={Buildings} hint={`${s.orgs_active} active`} testid="tile-orgs" />
                <Tile label="Users" value={s.users_total} icon={Users} testid="tile-users" />
                <Tile label="Forms" value={s.forms_total} icon={FilesIcon} testid="tile-forms" />
                <Tile label="Records" value={s.records_total} icon={Database} testid="tile-records" />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="brut-section p-8">
                    <div className="label-overline mb-2">Status breakdown</div>
                    <h2 className="text-xl font-bold tracking-tight mb-6">Organizations by state</h2>
                    <div className="space-y-3">
                        <Row icon={CheckCircle} label="Active" count={s.orgs_active} total={s.orgs_total} color="text-primary" />
                        <Row icon={Hourglass} label="Pending" count={s.orgs_pending} total={s.orgs_total} color="text-yellow-600" />
                        <Row icon={Pause} label="Suspended" count={s.orgs_suspended} total={s.orgs_total} color="text-destructive" />
                    </div>
                </div>
                <div className="brut-section p-8">
                    <div className="label-overline mb-2">Distribution</div>
                    <h2 className="text-xl font-bold tracking-tight mb-6">Plans</h2>
                    <div className="space-y-3">
                        {Object.entries(s.by_plan || {}).map(([k, v]) => (
                            <div key={k} className="flex items-center justify-between border-b border-border pb-3 last:border-b-0" data-testid={`plan-${k}`}>
                                <span className="capitalize font-medium">{k}</span>
                                <span className="font-mono text-lg">{v}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </PageContainer>
    );
}

function Row({ icon: Icon, label, count, total, color }) {
    const pct = total ? Math.round((count / total) * 100) : 0;
    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 text-sm"><Icon size={16} weight="duotone" className={color} /> {label}</div>
                <span className="font-mono text-sm">{count} · {pct}%</span>
            </div>
            <div className="h-1.5 bg-secondary rounded-sm overflow-hidden">
                <div className={`h-full ${color.replace("text-", "bg-")}`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}
